import * as Actions from '../actions/miscActions';
import {RestoreStatus, RestoreStatusType} from '../actions/miscActions';
import * as Backups from '../restore';
import {tabSession} from '../../Auth/session';
import ls from '../../lib/localStorage';
import pWaitFor from 'p-wait-for';
import {REFUSED_SAVE_DISMISSED, SAVE_REFUSED, refusedSaves, type RefusedSave} from '../../lib/refusedSaves';

// Helper functions to get firmId/userId from sessionStorage
// Note: Cannot import from currentLogin.ts due to circular dependency with store
const getFirmId = () => sessionStorage.getItem('firmId');
const getUserId = () => sessionStorage.getItem('userId');

/**
 * Should we do excessive logging?
 */
let logging = ls.getBoolean(ls.DIAGNOSTICS, false);
import type {LoginError} from '../actions/miscActions';

export type MiscState = {
    authenticationError: LoginError | null;
    /**
     * Monotonic counter incremented every time `requestLoginRetry()` is
     * dispatched. `usePostLoginOrchestration` watches this and re-runs the
     * post-login sequence when it changes, allowing the user to recover from
     * a failed initial workspace load without reloading the page.
     */
    loginRetryRequestId: number;
    expandedComponent: string | null;
    isConnected: boolean;
    isLoading: boolean;
    isAuthenticated: boolean;
    authenticatedFirmId: string | null;
    authenticatedUserId: string | null;
    isLoggingIn: boolean;
    isReadOnly: boolean;
    loadingMessage: string;
    quickAction: {
        taskToShow: any | null;
        oldTaskSaved: boolean;
    };
    portalUser: boolean;
    restoreStatus: RestoreStatusType;
    /** OWA-842: saves the server refused for good, for a notice to explain (lib/refusedSaves) */
    refusedSaves: RefusedSave[];
}

const initialState: MiscState = {
    authenticationError: null,
    loginRetryRequestId: 0,
    expandedComponent: null,
    isConnected: false,
    isLoading: false,
    isAuthenticated: false,
    authenticatedFirmId: null,
    authenticatedUserId: null,
    isLoggingIn: false,
    isReadOnly: false,
    loadingMessage: '',
    quickAction: {
        taskToShow: null,
        oldTaskSaved: false
    },
    portalUser: false,
    restoreStatus: Actions.NOT_RESTORED,
    refusedSaves: [],
};

function getRestoreStatusText(restoreStatus: RestoreStatusType) {
    switch (restoreStatus) {
        case RestoreStatus.NotRestored:
            return ({text: 'Not Restored', color: 'maroon'});
        case RestoreStatus.Restoring:
            return ({text: 'Restoring', color: 'orange'});
        case RestoreStatus.Restored:
            return ({text: 'Restored', color: 'green'});
        case RestoreStatus.Failed:
            return ({text: 'Restore Failed', color: 'maroon'});
    }
}

function misc(state: MiscState = initialState, action: any): MiscState {
    const st = {...state};

    switch (action.type) {
        // case LOGIN is handled in MiscSagas.js

        case Actions.CONNECTION_STATE: {
            st.isConnected = action.isConnected;
            return st;
        }

        case Actions.SET_RESTORE_STATUS: {
            if (logging) {
                let from = getRestoreStatusText(st.restoreStatus);
                let to = getRestoreStatusText(action.restoreStatus);

                if (action.restoreStatus === RestoreStatus.Restored || action.restoreStatus === RestoreStatus.Failed)
                    console.debug(`%c⮂ miscReducer: Changing restore status from ${from.text} to ${to.text}`, `color: ${to.color}`);
            }
            st.restoreStatus = action.restoreStatus;
            return st;
        }

        case Actions.SET_READ_ONLY: {
            st.isReadOnly = action.isReadOnly;
            return st;
        }

        case Actions.UPDATE_LOADING: {
            st.isLoading = action.isLoading;
            return st;
        }

        // -- Handle login --

        case Actions.LOGIN_RESULT: {
            st.isAuthenticated = action.payload.isAuthenticated;
            // Normalize: accept both string (legacy callers) and {title, message} (new callers)
            const rawErr = action.payload.authenticationError;
            st.authenticationError = typeof rawErr === 'string'
                ? (rawErr ? {title: 'Login Error', message: rawErr} : null)
                : rawErr || null;
            st.authenticatedFirmId = action.payload.firmId;
            st.authenticatedUserId = action.payload.userId;
            st.isLoggingIn = false;

            if (st.isAuthenticated && st.restoreStatus === Actions.NOT_RESTORED) {
                const operation = tabSession.capture();
                // Start the restore process - don't block UI, let backups load incrementally
                st.isLoading = false;
                st.restoreStatus = Actions.RESTORING;

                // Load backups - wait for firmId/userId to be available in sessionStorage
                (async () => {
                    try {
                        // Wait for firmId and userId to be set in sessionStorage
                        await pWaitFor(
                            () => getFirmId() === operation.identity.firmId && getUserId() === operation.identity.userId,
                            {timeout: 30_000, interval: 100, signal: operation.signal}
                        );

                        operation.assertCurrent();
                        const {firmId, userId} = operation.identity;

                        if (firmId && userId) {
                            Backups.config(firmId, userId);
                            // OWA-525: load() reports RESTORED or RESTORE_FAILED itself, after its retries
                            await Backups.load({authenticatedFirmId: firmId, authenticatedUserId: userId}, operation);
                            operation.assertCurrent();
                            console.info('%c⮂ miscReducer: ✔ Backups load finished', 'color: green');
                        }
                        else {
                            // No account to load backups for: there is nothing to wait for
                            const {default: store} = await import('../store');
                            operation.assertCurrent();
                            store.dispatch(Actions.setRestoreStatus(Actions.RESTORED));
                        }
                    }
                    catch (err) {
                        console.error(`%c⮂ miscReducer: Backups not loaded. Error: ${err.message}`, 'color: maroon', err);
                        // OWA-525: a load that throws is a failed load, never RESTORED: code waiting
                        // for RESTORED (Dashboard draft recovery, options, the queue) must not act on
                        // empty lists. A stale session reports nothing.
                        try { operation.assertCurrent(); } catch { return; }
                        // The status, the notice and the full fetch that fills the lists again
                        Backups.reportRestoreFailed();
                    }
                })();
            }
            else {
                st.isLoading = false;
                st.restoreStatus = Actions.RESTORED;
            }

            return st;
        }
        case Actions.LOGIN_ERROR_RECOGNIZED: {
            st.authenticationError = null;

            return st;
        }
        case Actions.LOGIN_RETRY_REQUESTED: {
            // Bump the counter so usePostLoginOrchestration re-runs, and
            // clear the prior error so the Home page returns to the loading
            // state while the retry is in flight.
            st.loginRetryRequestId = (st.loginRetryRequestId ?? 0) + 1;
            st.authenticationError = null;

            return st;
        }
        case Actions.LOGIN_STATE: {
            st.isLoggingIn = action.isLoggingIn;

            return st;
        }
        case Actions.SET_LOADING_MESSAGE: {
            st.loadingMessage = action.loadingMessage;

            return st;
        }

        case Actions.SET_FIRM_ID: {
            st.authenticatedFirmId = action.firmId;

            return st;
        }

        case Actions.SET_USER_ID: {
            st.authenticatedUserId = action.userId;

            return st;
        }

        case Actions.SET_PORTAL_USER: {
            st.portalUser = action.portalUser;

            return st;
        }

        // -- Handle quickActions

        // This is called first, by ActionButton.
        case Actions.SAVE_OPEN_TASK: {
            // TaskItem is looking for taskToShow/!null and oldTaskSaved/false to do autosave
            st.quickAction = {
                taskToShow: action.task, // This is the task we want to eventually show
                oldTaskSaved: false      // The old task has not been saved yet
            };

            return st;
        }

        // This is called second, by TaskItem.  The change will trigger TaskGrid to show the new task.
        case Actions.SHOW_TASK: {
            // TaskGrid is looking for oldTaskSaved/true and taskToShow/!null to call onEdit(taskToShow)
            st.quickAction = {
                taskToShow: action.task,
                oldTaskSaved: true  // The old task has been saved!
            };

            return st;
        }

        // This is called third, by TaskGrid, after opening the new task
        case Actions.SHOW_TASK_HANDLED: {
            // Reset quickAction
            st.quickAction = {
                taskToShow: null,    // Set to inactive
                oldTaskSaved: false  // Set to inactive
            };

            return st;
        }

        case Actions.SET_EXPANDED_COMPONENT: {
            st.expandedComponent = action.component;

            return st;
        }

        // OWA-842: saves the server refused for good (lib/refusedSaves)
        case SAVE_REFUSED:
        case REFUSED_SAVE_DISMISSED: {
            const list = refusedSaves(state.refusedSaves, action);
            if (list === state.refusedSaves) return state;
            st.refusedSaves = list;
            return st;
        }

        default:
            return state;
    }
}

export default misc;
